import { useState, useMemo } from 'react'
import { useProductStore } from '../store/productStore'
import { useDebounce } from './useDebounce'
import type { Product } from '../types'

export type SortField = 'none' | 'price' | 'quantity'
export type SortDir   = 'asc' | 'desc'
export type AdminStatusFilter = Product['status'] | 'all'

export const statusOptions: { value: AdminStatusFilter; label: string }[] = [
  { value: 'all',       label: 'כל הסטטוסים' },
  { value: 'active',    label: 'מכירה פעילה' },
  { value: 'sale',      label: 'מבצע' },
  { value: 'sold_out',  label: 'אזל' },
  { value: 'suspended', label: 'מושהה' },
  { value: 'draft',     label: 'טיוטה' },
  { value: 'other',     label: 'אחר' },
]

export function useAdminFilters() {
  const { products } = useProductStore()

  const [search, setSearch]             = useState('')
  const [statusFilter, setStatusFilter] = useState<AdminStatusFilter>('all')
  const [sortField, setSortField]       = useState<SortField>('none')
  const [sortDir, setSortDir]           = useState<SortDir>('asc')

  const debouncedSearch = useDebounce(search, 300)

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('asc') }
  }

  const sortIcon = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setSortField('none')
  }

  const hasActiveFilters = search !== '' || statusFilter !== 'all' || sortField !== 'none'

  const filtered = useMemo(() => {
    let result = products
      .filter(p => statusFilter === 'all' || p.status === statusFilter)
      .filter(p =>
        p.name.includes(debouncedSearch) ||
        p.description?.includes(debouncedSearch)
      )

    if (sortField === 'price')    result = [...result].sort((a, b) => sortDir === 'asc' ? a.price - b.price : b.price - a.price)
    if (sortField === 'quantity') result = [...result].sort((a, b) => sortDir === 'asc' ? a.quantity - b.quantity : b.quantity - a.quantity)

    return result
  }, [products, statusFilter, debouncedSearch, sortField, sortDir])

  return {
    search, setSearch,
    statusFilter, setStatusFilter,
    sortField, sortDir,
    toggleSort, sortIcon,
    clearFilters, hasActiveFilters,
    filtered,
    totalCount: products.length,
  }
}